import { useState } from 'react';
import type { OrganizationStatus, OrganizationType } from '@dsr/shared-types';
import {
  useOrganizations,
  type OrganizationFilters,
} from '../api/queries/clients';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { Button, Select, type SelectOption } from '../components/ui';
import { DirectoryView } from '../components/directory/DirectoryView';
import type { DirectoryEntry } from '../components/directory/types';
import { initialsOf } from '../components/directory/initials';
import { ORG_STATUSES, ORG_TYPES, typeLabel } from '../components/clients/meta';
import { CreateOrganizationModal } from '../components/clients/CreateOrganizationModal';

// Businesses = every non-HNI client organization (HNI individuals live under
// Directory › Individuals). The type filter therefore excludes `hni`.
const BUSINESS_TYPES = ORG_TYPES.filter((t) => t.value !== 'hni');

export function DirectoryBusinessesPage(): JSX.Element {
  const [filters, setFilters] = useState<OrganizationFilters>({});
  const [qDraft, setQDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, error } = useOrganizations(filters);
  const canWrite = can('org.write');

  const entries: DirectoryEntry[] = (data?.organizations ?? [])
    .filter((o) => o.type !== 'hni')
    .map((o): DirectoryEntry => ({
      id: `org-${o.id}`,
      kind: 'business',
      name: o.name,
      subtitle: o.legal_name,
      to: `/organizations/${o.id}`,
      initials: initialsOf(o.name),
      badges: [
        { label: typeLabel(o.type) },
        { label: o.status, className: `badge-${o.status}` },
      ],
      meta: [{ label: 'Industry', value: o.industry ?? '—' }],
    }));

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...ORG_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];
  const typeFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    ...BUSINESS_TYPES.map((t) => ({ value: t.value, label: t.label })),
  ];

  return (
    <>
      <DirectoryView
        title="Businesses"
        subtitle="Corporate, government, and other client organizations."
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New client
            </Button>
          )
        }
        filters={
          <>
            <form
              className="filter-search"
              onSubmit={(e) => {
                e.preventDefault();
                setFilters((f) => ({ ...f, q: qDraft }));
              }}
            >
              <input
                type="search"
                className="form-input"
                placeholder="Search by name…"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
              />
            </form>
            <Select
              value={filters.status ?? 'all'}
              onValueChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  status: v === 'all' ? undefined : (v as OrganizationStatus),
                }))
              }
              options={statusFilterOptions}
              aria-label="Filter by status"
            />
            <Select
              value={filters.type ?? 'all'}
              onValueChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  type: v === 'all' ? undefined : (v as OrganizationType),
                }))
              }
              options={typeFilterOptions}
              aria-label="Filter by type"
            />
          </>
        }
        entries={entries}
        isLoading={isLoading}
        isError={isError}
        errorText={
          error instanceof ApiError ? error.message : 'Failed to load clients.'
        }
        emptyText="No businesses match the current filters."
      />
      {createOpen && (
        <CreateOrganizationModal onClose={() => setCreateOpen(false)} />
      )}
    </>
  );
}
